import {match} from 'ts-pattern';

import type {ConfiguratorBundleResponseBody} from '@dms/api/new-vehicle';

import {isSelectionConditionMet} from '../../../utils/isSelectionConditionMet';

export type ExclusionConflictInput = {
  features: Pick<
    ConfiguratorBundleResponseBody['features'][number],
    'id' | 'standaloneOrderable'
  >[];
  packages: Pick<ConfiguratorBundleResponseBody['packages'][number], 'id' | 'memberFeatureIds'>[];
  exclusions: ConfiguratorBundleResponseBody['featureExclusions'];
  variantId: string;
  featureIds: string[];
  packageIds: string[];
  /** The last evaluation's auto-added features and packages. */
  autoAddedIds: ReadonlySet<string>;
  /** A partner the configurator holds in place whatever the selection (locked wheel, freeze). */
  isPinned: (featureId: string) => boolean;
  canRemovePackages: boolean;
};

/** Features an active `excludes` rule keeps apart from `featureId`, in either direction. */
const getExclusionPartners = (
  featureId: string,
  exclusions: ConfiguratorBundleResponseBody['featureExclusions'],
  variantId: string,
  build: string[]
) =>
  exclusions
    .filter((exclusion) => isSelectionConditionMet(exclusion.appliesIf, variantId, build))
    .flatMap(({sourceFeatureId, targetFeatureId}) =>
      match(featureId)
        .with(sourceFeatureId, () => [targetFeatureId])
        .with(targetFeatureId, () => [sourceFeatureId])
        .otherwise(() => [])
    );

/**
 * What adding `addedFeatureIds` (a feature, or a package's members) does under the active
 * excludes rules, judged against the build the evaluator sees: selected features, members of the
 * packages in the build and auto-added ids. Last click wins (D71): a partner the user picked, on
 * its own or through selected packages, leaves with those packages and the package-only members
 * no remaining package supplies. Any other partner is kept and blocks the addition.
 */
export const getExclusionConflict = (addedFeatureIds: string[], input: ExclusionConflictInput) => {
  const {featureIds, packageIds} = input;
  const packagesInBuild = input.packages.filter(
    ({id}) => packageIds.includes(id) || input.autoAddedIds.has(id)
  );
  const build = [
    ...featureIds,
    ...input.autoAddedIds,
    ...packagesInBuild.flatMap(({memberFeatureIds}) => memberFeatureIds),
  ];
  const candidate = [...build, ...addedFeatureIds];
  // The click can also complete the condition of a rule between two features already in the
  // build. Neither end is the one clicked, so neither replaces the other: both are kept.
  const triggeredIds = input.exclusions
    .filter(
      (exclusion) =>
        [exclusion.sourceFeatureId, exclusion.targetFeatureId].every(
          (id) => build.includes(id) && !addedFeatureIds.includes(id)
        ) &&
        isSelectionConditionMet(exclusion.appliesIf, input.variantId, candidate) &&
        !isSelectionConditionMet(exclusion.appliesIf, input.variantId, build)
    )
    .flatMap(({sourceFeatureId, targetFeatureId}) => [sourceFeatureId, targetFeatureId]);
  const partnerIds = [
    ...new Set([
      ...addedFeatureIds.flatMap((id) =>
        getExclusionPartners(id, input.exclusions, input.variantId, candidate)
      ),
      ...triggeredIds,
    ]),
  ].filter((id) => build.includes(id) && !addedFeatureIds.includes(id));
  const partners = partnerIds.map((id) => {
    const suppliers = packagesInBuild.filter(({memberFeatureIds}) => memberFeatureIds.includes(id));
    const supplierIds = suppliers
      .map((item) => item.id)
      .filter((item) => packageIds.includes(item));
    const isPickedByUser = featureIds.includes(id) || supplierIds.length > 0;
    const isKept =
      triggeredIds.includes(id) ||
      !isPickedByUser ||
      input.isPinned(id) ||
      suppliers.length > supplierIds.length ||
      (supplierIds.length > 0 && !input.canRemovePackages);
    return {id, packageIds: supplierIds, isKept};
  });
  const removedPackageIds = [...new Set(partners.flatMap((partner) => partner.packageIds))];
  const remainingMemberIds = [
    ...addedFeatureIds,
    ...input.packages
      .filter(({id}) => packageIds.includes(id) && !removedPackageIds.includes(id))
      .flatMap(({memberFeatureIds}) => memberFeatureIds),
  ];
  const orphanIds = input.packages
    .filter(({id}) => removedPackageIds.includes(id))
    .flatMap(({memberFeatureIds}) => memberFeatureIds)
    .filter(
      (id) =>
        !remainingMemberIds.includes(id) &&
        input.features.find((feature) => feature.id === id)?.standaloneOrderable === false
    );
  return {
    partners,
    isBlocked: partners.some(({isKept}) => isKept),
    removedFeatureIds: featureIds.filter((id) => partnerIds.includes(id) || orphanIds.includes(id)),
    removedPackageIds,
  };
};
